/* @flow */
import { cloneDeep } from 'lodash';

import apiRequest from 'lib/apiRequest';
import Activity from 'entities/Activity';
import ChatSender from 'entities/ChatSender';
import Entity from 'lib/Entity';
import File from 'entities/File';
import Group from 'entities/Group';
import Message from 'entities/Message';
import MessageSender from 'entities/MessageSender';
import ReactionSummary from 'entities/ReactionSummary';
import User from 'entities/User';
import uuid from 'utils/uuid';

import ChatMessageActions from 'chat/actions/ChatMessageActions';

export class ChatBaseMessage extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
  }

  getSeq(): ?number {
    return this.get('seq');
  }

  setChatId(id: string) {
    const chat = this.get('chat');
    chat.uuid = id;
    this.set('chat', chat);
  }

  getChatId(): string {
    return this.get('chat').uuid;
  }

  getType(): string {
    return this.get('type');
  }

}

export class ChatMessage extends ChatBaseMessage {
  defaults() {
    return {
      body: '',
      preview: '',
      created_at: new Date(),
      type: 'text',
    };
  }

  urlRoot(): string {
    return `/chats/${this.getChatId()}/messages`;
  }

  parse(response: Object) {
    const message = cloneDeep(response);
    if ('created_at' in message) {
      message['created_at'] = new Date(message['created_at']);
    }
    message['sender'] = ChatSender.fromJSON(message['sender']);

    if ('message' in message) {
      message['message'] = Message.fromJSON(message['message']);
    }

    if (message['file']) {
      message['file'] = File.fromJSON(message['file']);
    }

    if (message['group_summary']) {
      message['group_summary'] = Group.fromJSON(message['group_summary']);
    }

    if (message['payment_req']) {
      message['payment_req'] = Activity.fromJSON(message['payment_req']);
    }

    if (message['reaction_summary']) {
      message['reaction_summary'] =
        ReactionSummary.fromJSON(message['reaction_summary']);
    }

    return message;
  }

  setReactionSummary(reactionSummary: ReactionSummary) {
    // Only update if reaction summary is newer than current
    if (reactionSummary.getVersion() > this.getReactionSummary().getVersion()) {
      this.set('reaction_summary', reactionSummary);
    }
  }

  getReactionSummary(): ReactionSummary {
    return this.get('reaction_summary') || new ReactionSummary();
  }

  hasReactions(): boolean {
    return !!this.getReactionSummary().getReactions().length;
  }

  addReaction(reactionName: string): void {
    ChatMessageActions.addReaction(this, reactionName);
  }

  removeReaction(reactionName: string): void {
    ChatMessageActions.removeReaction(this, reactionName);
  }

  getActivity() {
    return this.get('payment_req');
  }

  getSender(): (ChatSender|User|MessageSender) {
    return this.get('sender');
  }

  getGroupSummary() {
    return this.get('group_summary');
  }

  getGroupIds(): Array<number> {
    return this.getGroupSummary() ? [this.getGroupSummary().id] : [];
  }

  getBody(): ?string {
    return this.get('body');
  }

  setPreview(body: ?string) {
    return this.set({ preview: body });
  }

  getPreview() {
    return this.get('preview') || this._generatePreview();
  }

  _generatePreview() {
    const preview = (
      this.getBody() ||
      (this.getFile() && '1 Attachment 📚') ||
      '<error>'
    );

    if (this.getType() === 'text') {
      return `You: ${preview}`;
    } else if (this.getType() === 'broadcast') {
      return `Announcement: ${preview}`;
    }
    return preview;
  }

  getISO639Locale() {
    let locale = this.get('locale');

    if (locale) {
      locale = locale.split('-')[0];
    }

    return locale;
  }

  getFile() {
    return this.get('file');
  }

  getMessage(): Message {
    return this.get('message');
  }

  getCreatedAt() {
    return this.get('created_at');
  }

  getSentAt() {
    return this.getCreatedAt();
  }

  getReadSummary() {
    return this.get('read_summary');
  }

  getUuid() {
    return this.get('uuid');
  }

  getOriginGroupId() {
    return this.get('origin_group_id');
  }

  send() {
    return this.save({}, {
      url: this.urlRoot(),
      method: 'create',
      data: {
        message: {
          body: this.getBody(),
          uuid: this.id,
          file: this.getFile(),
          origin_group_id: this.getOriginGroupId(),
        },
      },
    });
  }

  static findByUuid(chatThreadUuid, chatMessageUuid, trackingParameter): Promise<ChatMessage> {
    return apiRequest(`/chats/${chatThreadUuid}/messages/${chatMessageUuid}`, 'GET',
      { _t: trackingParameter }
    ).then((chatMessageJSON) => {
      return ChatMessage.fromJSON(chatMessageJSON);
    });
  }

  static create(text, files, chat, sender, seq, type = 'text', groupSummary, originGroupId) {
    return new ChatMessage({
      uuid: uuid(),
      sender,
      chat: { uuid: chat.id },
      body: text,
      failed: false,
      seq,
      file: files[0],
      type,
      group_summary: groupSummary,
      origin_group_id: originGroupId,
    });
  }
}

export class ChatSystemMessage extends ChatMessage {
  getBodyAttributes() {
    return this.get('body_attributes');
  }
}

export class ChatGapMessage extends ChatBaseMessage {
  getParams() {
    return this.get('params');
  }

  getUuid() {
    return 'gap-uuid';
  }
}

export function chatMessageFactory(data: Object) {
  if (data.type === 'system') {
    return ChatSystemMessage.fromJSON(data);
  } else {
    return ChatMessage.fromJSON(data);
  }
}

export function shouldListenToPushEventFrom(message: ChatMessage): boolean {
  return ['text', 'broadcast', 'system'].includes(message.getType()) && !!message.get('chat');
}

export const chatMessageTypes = {
  gap: ChatGapMessage,
  system: ChatSystemMessage,
  attachment: ChatMessage,
  text: ChatMessage,
  broadcast: ChatMessage,
};

export default ChatMessage;



// WEBPACK FOOTER //
// ./src/entities/ChatMessage.js